/**
 * Custom hook for managing cases data and operations
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  fetchCases, 
  fetchCaseById, 
  createCase, 
  updateCase, 
  deleteCase 
} from '../api/caseApi';
import { Case, NewCaseData, UpdateCaseData } from '../types';

/**
 * Hook for managing a list of cases
 * @returns Object containing cases data and operations
 */
export function useCases() {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [sortField, setSortField] = useState<keyof Case>('case_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * Load all cases from the API
   */
  const loadCases = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchCases();
      setCases(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch cases'));
      console.error('Error fetching cases:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new case
   * @param caseData Data for the new case
   */
  const addCase = useCallback(async (caseData: NewCaseData) => {
    setError(null);
    
    try {
      const newCase = await createCase(caseData);
      setCases(prev => [newCase, ...prev]);
      return newCase;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create case'));
      console.error('Error creating case:', err);
      throw err;
    }
  }, []);

  /**
   * Update an existing case
   * @param id ID of the case to update
   * @param caseData Updated case data
   */
  const updateCaseItem = useCallback(async (id: string, caseData: UpdateCaseData) => {
    setError(null);
    
    try {
      const updatedCase = await updateCase(id, caseData);
      setCases(prev => 
        prev.map(c => c.id === id ? updatedCase : c)
      );
      return updatedCase;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update case'));
      console.error('Error updating case:', err);
      throw err;
    }
  }, []);

  /**
   * Delete a case
   * @param id ID of the case to delete
   */
  const removeCase = useCallback(async (id: string) => {
    setError(null);
    
    try {
      await deleteCase(id);
      setCases(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete case'));
      console.error('Error deleting case:', err);
      throw err;
    }
  }, []);

  /**
   * Sort cases by the specified field and direction
   * @param field Field to sort by
   */
  const handleSort = useCallback((field: keyof Case) => {
    setSortField(field);
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortDirection('asc');
    }
  }, [sortField]);

  /**
   * Filter cases by search term
   * @param term Search term
   */
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  // Load cases on initial render
  useEffect(() => {
    loadCases();
  }, [loadCases]);

  // Filter and sort cases
  const filteredAndSortedCases = useMemo(() => {
    let result = [...cases];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c => 
        c.title.toLowerCase().includes(term) || 
        c.client_name.toLowerCase().includes(term) ||
        c.description.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      // Special handling for case_number to ensure proper numeric sorting
      if (sortField === 'case_number') {
        // Extract numeric part if case numbers follow a pattern like CASE-123456
        const aNum = aValue ? aValue.toString().replace(/^CASE-/, '') : '';
        const bNum = bValue ? bValue.toString().replace(/^CASE-/, '') : '';
        
        return sortDirection === 'asc' 
          ? aNum.localeCompare(bNum, undefined, { numeric: true }) 
          : bNum.localeCompare(aNum, undefined, { numeric: true });
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      
      if (aValue === null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue === null) return sortDirection === 'asc' ? 1 : -1;
      
      return sortDirection === 'asc' 
        ? (aValue as any) - (bValue as any) 
        : (bValue as any) - (aValue as any);
    });
    
    return result;
  }, [cases, searchTerm, sortField, sortDirection]);

  return {
    cases: filteredAndSortedCases,
    loading,
    error,
    sortField,
    sortDirection,
    searchTerm,
    loadCases,
    addCase,
    updateCaseItem,
    removeCase,
    handleSort,
    handleSearch
  };
}

/**
 * Hook for managing a single case
 * @param id ID of the case to manage
 * @returns Object containing case data and operations
 */
export function useCase(id: string | null) {
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load case data from the API
   */
  const loadCase = useCallback(async () => {
    if (!id) {
      setCaseData(null);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchCaseById(id);
      setCaseData(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch case'));
      console.error('Error fetching case:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  /**
   * Update the current case
   * @param caseData Updated case data
   */
  const updateCurrentCase = useCallback(async (caseData: UpdateCaseData) => {
    if (!id || !caseData) return;
    
    setError(null);
    
    try {
      const updatedCase = await updateCase(id, caseData);
      setCaseData(updatedCase);
      return updatedCase;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update case'));
      console.error('Error updating case:', err);
      throw err;
    }
  }, [id]);

  // Load case on initial render or when ID changes
  useEffect(() => {
    loadCase();
  }, [loadCase]);

  return {
    caseData,
    loading,
    error,
    loadCase,
    updateCase: updateCurrentCase
  };
}
